import { useTranslation } from 'react-i18next';

import { Checkbox } from '@/components/ui/checkbox';
import { Command, CommandItem, CommandList } from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn, formatDecimal } from '@/lib/utils';
import { FR_VAT_RATES, isSameVatRate } from '@/lib/utils/vat-rates';
import { Check, ChevronsUpDown } from 'lucide-react';

export type VatRateCellProps = {
  /** Deal item id, used to pair the margin checkbox with its label. */
  dealItemId: string;
  /**
   * Rate to render: a number for a real rate, a string for an aggregated parent
   * marker (`'≠'`), `null` when there is nothing to show.
   */
  displayValue: number | string | null | undefined;
  /** Drives the rate display: a margin-VAT item shows no percentage. */
  hasVatMargin: boolean;
  /**
   * Checked state of the margin checkbox. On a parent row it aggregates the
   * children, which is why it is not simply {@link hasVatMargin}.
   */
  isMarginChecked: boolean;
  isParent: boolean;
  isOfferLocked: boolean;
  isRecentlyModified: boolean;
  isEmpty?: boolean;
  onToggleVatMargin: (dealItemId: string) => void;
  /**
   * Whether the "TVA sur marge" checkbox is offered. The table needs it inline;
   * a review surface showing the rate as evidence does not.
   */
  showMarginToggle?: boolean;
  rateSelection?: {
    isEditing: boolean;
    onOpen: () => void;
    onClose: () => void;
    onSelect: (rate: number) => void;
  };
};

/**
 * The VAT cell: the rate plus the "TVA sur marge" checkbox that goes with it.
 *
 * Shared between the main table column and the VAT review dialog so both render
 * the same control — a rate shown one way in the table and another in the dialog
 * would be a bug waiting to happen, since the dialog exists precisely to judge
 * that rate.
 */
export const VatRateCell: React.FC<VatRateCellProps> = ({
  dealItemId,
  displayValue,
  hasVatMargin,
  isMarginChecked,
  isParent,
  isOfferLocked,
  isRecentlyModified,
  isEmpty = false,
  onToggleVatMargin,
  showMarginToggle = true,
  rateSelection,
}) => {
  const { t } = useTranslation();

  const rateLabel = isEmpty
    ? t('kpi.vat_required')
    : typeof displayValue === 'number' && !hasVatMargin
      ? `${formatDecimal(displayValue)}%`
      : typeof displayValue === 'string'
        ? displayValue
        : '—';

  return (
    <div
      className={cn(
        'h-full w-full text-right',
        (isParent || isOfferLocked) && 'opacity-50',
        isParent || !isOfferLocked ? 'cursor-pointer' : 'cursor-not-allowed',
        'border-l-2',
        isEmpty
          ? 'border-red-500 bg-red-50 text-red-600'
          : isRecentlyModified
            ? 'border-blue-500 bg-blue-50'
            : 'border-transparent'
      )}
    >
      <div className="flex h-full w-full items-center justify-between px-2 text-sm">
        {rateSelection ? (
          <Popover
            open={rateSelection.isEditing}
            onOpenChange={(open) => {
              if (!open) rateSelection.onClose();
            }}
          >
            <PopoverTrigger
              asChild
              disabled={isOfferLocked}
              onClick={() => !isOfferLocked && rateSelection.onOpen()}
            >
              <button
                type="button"
                className="flex items-center gap-1 disabled:cursor-not-allowed"
              >
                <span className="w-14 text-right">{rateLabel}</span>
                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[120px] p-0">
              <Command>
                <CommandList>
                  {FR_VAT_RATES.map((rate) => (
                    <CommandItem
                      key={rate}
                      value={String(rate)}
                      className="cursor-pointer"
                      onSelect={() => rateSelection.onSelect(rate)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          typeof displayValue === 'number' &&
                            isSameVatRate(displayValue, rate)
                            ? 'opacity-100'
                            : 'opacity-0'
                        )}
                      />
                      {`${formatDecimal(rate)}%`}
                    </CommandItem>
                  ))}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        ) : (
          <span className="w-14 text-right">{rateLabel}</span>
        )}
        {showMarginToggle && (
          <div className="flex items-center gap-1">
            <Checkbox
              defaultChecked={isMarginChecked}
              onCheckedChange={() => onToggleVatMargin(dealItemId)}
              // A locked offer rejects the mutation server-side
              // (`dealNotEditable`): leaving the box clickable only bought the
              // user an optimistic flip followed by a rollback and a toast.
              disabled={isParent || isOfferLocked}
              id={`${isParent ? 'parent-' : ''}tvasm-${dealItemId}`}
            />
            <label
              className="cursor-pointer text-xs"
              htmlFor={`${isParent ? 'parent-' : ''}tvasm-${dealItemId}`}
            >
              {t('kpi.vat_ms')}
            </label>
          </div>
        )}
      </div>
    </div>
  );
};
